import "../Extensions/ArrayExtensions";
import { BreadcrumbMap } from "../Models/BreadcrumbMap";
import { BreadcrumbData } from "../Models/BreadcrumbData";

const uuid = require("uuid");

interface MatchData {
  isVariable: boolean;
  match: boolean;
}

interface MapPathResult {
  template: string;
  variables: Map<string, string>;
  replacements: Map<string, string>;
}

class BreadcrumbService {
  createBreadcrumbs = async (
    path: string,
    breadcrumbsMap: BreadcrumbMap,
    variableResolver: (key: string, variables: Map<string, string>) => Promise<string | undefined>
  ) => {
    const mapResult = this.mapPath(path, breadcrumbsMap);

    if (mapResult) {
      return this.assembleBreadcrumbs(mapResult, breadcrumbsMap, variableResolver);
    }

    return [];
  };

  mapPath = (path: string, breadcrumbsMap: BreadcrumbMap): MapPathResult | undefined => {
    const currentItems = this.splitPath(path);
    const breadcrumbsKeys = Object.keys(breadcrumbsMap);

    for (let i = 0; i < breadcrumbsKeys.length; i++) {
      const keyItems = this.splitPath(breadcrumbsKeys[i]);

      if (currentItems.length === keyItems.length) {
        const variables = new Map<string, string>();
        const matchData: MatchData[] = [];

        for (let j = 0; j < currentItems.length; j++) {
          if (keyItems[j].startsWith(":") && keyItems[j] !== currentItems[j]) {
            variables.set(keyItems[j], currentItems[j]);
            matchData.push({ isVariable: true, match: false });
          } else if (!keyItems[j].startsWith(":") && keyItems[j] === currentItems[j]) {
            matchData.push({ isVariable: false, match: true });
          } else {
            matchData.push({ isVariable: false, match: false });
          }
        }

        if (this.isMatch(matchData)) {
          return {
            template: breadcrumbsMap[breadcrumbsKeys[i]].path,
            variables,
            replacements: new Map<string, string>(
              (breadcrumbsMap[breadcrumbsKeys[i]].replacements || []).map(obj => [
                obj.key,
                obj.value
              ])
            )
          };
        }
      }
    }

    return undefined;
  };

  isMatch = (matchData: MatchData[]) => {
    return matchData.every(data => {
      // Cannot compare template variable names with path's resolved values, they will never match
      if (data.isVariable) {
        return true;
      }

      return data.match;
    });
  };

  assembleBreadcrumbs = async (
    mapResult: MapPathResult,
    breadcrumbsMap: any,
    variableResolver: (key: string, variables: Map<string, string>) => Promise<string | undefined>
  ) => {
    const sections: BreadcrumbData[] = [];
    const items: string[] = [];
    const { variables, template, replacements } = mapResult;

    await this.splitPath(template).forEachAsync(async (item, index) => {
      items.push(item);

      const resolvedItem = this.resolvePathReplacements(
        item.startsWith(":") ? (await variableResolver(item, variables)) || "" : item,
        replacements
      );

      const templatePath = this.createPath(items);
      const linkPath = this.resolvePathVariables(templatePath, variables);

      const breadcrumb = {
        key: `${resolvedItem}_${uuid()}`,
        isLink: !!breadcrumbsMap[templatePath],
        linkPath: breadcrumbsMap[templatePath] ? linkPath : "#",
        name: resolvedItem,
        order: index
      };

      sections.push(breadcrumb);
    });

    return sections;
  };

  /**
   * Assembles a list of tokens into a proper path
   */
  createPath = (items: string[]) => {
    return items.reduce((path, item, index) => {
      return path.concat(item).concat(index !== items.length - 1 ? "/" : "");
    }, "/");
  };

  /**
   * Replace variables in the path i.e. ":code, :id" with the values defined in the provided map
   */
  resolvePathVariables = (templatePath: string, variables: Map<string, string>) => {
    return Array.from(variables.keys()).reduce((path, variableName) => {
      return path.replace(
        variableName,
        this.defaultResolver(variableName, variables) || variableName
      );
    }, templatePath);
  };

  /**
   * Convert variables to their actual values as defined in the replacements map
   */
  resolvePathReplacements = (path: string, replacements: Map<string, string>) => {
    return Array.from(replacements.keys()).reduce((replaced, key) => {
      return replaced.replace(key, replacements.get(key) || key);
    }, path);
  };

  defaultResolver = (key: string, variables: Map<string, string>) => {
    return variables.get(key) || key;
  };

  splitPath = (path: string) => {
    return path.split("/").filter(item => item.length > 0);
  };
}

export const breadcrumbService = new BreadcrumbService();
